import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';

import { EnumNgEventName } from '../../shared/event-animation.data';
import { NgContinuousAnimationService } from '../../shared/services/continuous-animation.service';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation Definition
 */
function pulseGrow(scale: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	scale = scale ? scale : 1.1;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility	: AUTO_STYLE,
				transform	: 'scale3d(1, 1, 1)',
				easing		: 'ease',
				offset		: 0
			}),
			style({
				transform	: `scale3d(${scale}, ${scale}, ${scale})`,
				easing		: 'ease',
				offset		: 0.5
			}),
			style({
				transform	: 'scale3d(1, 1, 1)',
				easing		: 'ease',
				offset		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * PulseGrow on Event
 */
@Directive({
    selector: '[ngPulseGrowOnEvent]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgPulseGrowOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement					  = null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale value is relative to the animation functionality
		if (this.effectOptions?.scale && this.effectOptions?.scale < 0) throw new Error(`PulseGrowOnEventDirective => ngOnInit => FATAL ERROR: Provided scale value should be a positive number: ${this.effectOptions?.scale}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: pulseGrow(this.effectOptions?.scale),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * PulseGrow on Mouse Hover
 */
@Directive({
    selector: '[ngPulseGrowOnMouseHover]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgPulseGrowOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement						= null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale value is relative to the animation functionality
		if (this.timingOptions?.scale && this.timingOptions?.scale < 0) throw new Error(`PulseGrowOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided scale value should be a positive number: ${this.timingOptions?.scale}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: pulseGrow(this.timingOptions?.scale),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	scale : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	scale : number;
}
